import { useState } from "react";
import toast from "react-hot-toast";
import Modal from "@/components/Modal";
import { RegisterSession } from "@/redux/services/pos/pos.type";
import { useCloseSessionMutation } from "@/redux/services/pos/registerSession.api";
import { displayPrice, formatVariance, parseMoneyToCents } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { hasApiErrorKey, toastApiError } from "@/lib/apiErrors";

interface CloseSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  session: RegisterSession;
  currency: string;
}

const CloseSessionModal = ({
  isOpen,
  onClose,
  session,
  currency,
}: CloseSessionModalProps) => {
  const [actualCash, setActualCash] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  const [closeSession, { isLoading }] = useCloseSessionMutation();

  const actualCashCents = parseMoneyToCents(actualCash) ?? 0;
  const expectedCashCents = session.expectedCash ?? 0;
  const varianceCents = actualCashCents - expectedCashCents;

  const getVarianceColor = () => {
    if (varianceCents > 0) return "text-green-600";
    if (varianceCents < 0) return "text-red-600";
    return "text-muted-foreground";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const cashCents = parseMoneyToCents(actualCash);
    if (cashCents === null || cashCents < 0) {
      toast.error("Veuillez entrer un montant valide");
      return;
    }

    try {
      await closeSession({
        sessionUuid: session.id,
        data: {
          actualCash: cashCents,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
      }).unwrap();

      toast.success("Caisse fermée avec succès");
      onClose();
    } catch (error: unknown) {
      if (hasApiErrorKey(error, "cash_register.session.already_closed")) {
        toast.error("Cette caisse est déjà fermée.");
        onClose();
        return;
      }
      toastApiError(
        error,
        "Une erreur s'est produite lors de la fermeture de la caisse"
      );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Fermer la caisse">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="actualCash"
            className="block text-sm font-medium text-card-foreground mb-1"
          >
            Espèces comptées
          </label>
          <input
            id="actualCash"
            type="text"
            inputMode="numeric"
            placeholder="0"
            value={actualCash}
            onChange={(e) => setActualCash(e.target.value)}
            className="w-full rounded-lg border border-border px-4 py-3 text-lg focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div>
          <label
            htmlFor="closeNotes"
            className="block text-sm font-medium text-card-foreground mb-1"
          >
            Notes (optionnel)
          </label>
          <textarea
            id="closeNotes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Notes..."
            className="w-full rounded-lg border border-border px-4 py-3 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="rounded-lg bg-muted/50 p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Attendu</span>
            <span className="font-medium">
              {displayPrice(expectedCashCents, currency)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Compté</span>
            <span className="font-medium">
              {displayPrice(actualCashCents, currency)}
            </span>
          </div>
          <hr className="border-border" />
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Écart</span>
            <span className={`font-medium ${getVarianceColor()}`}>
              {formatVariance(varianceCents / 100, currency)}
            </span>
          </div>
        </div>

        <Button
          type="submit"
          variant="destructive"
          size="xl"
          className="w-full"
          disabled={isLoading}
          isLoading={isLoading}
        >
          Confirmer la fermeture
        </Button>
      </form>
    </Modal>
  );
};

export default CloseSessionModal;
